import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function refreshMe(signal) {
    try {
      const { data } = await api.get("/auth/me", { signal });
      if (data && data.authenticated === false) {
        setUser(null);
        setLoading(false);
        return null;
      }
      setUser(data);
      setLoading(false);
      return data;
    } catch (e) {
      // When an in-flight request is cancelled (StrictMode double-mount,
      // route change, etc.) a *second* refreshMe is typically about to run.
      // We must NOT flip loading=false here, otherwise RoleRoute sees
      // user=null before the fresh request resolves and redirects to /login.
      if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") {
        return null;
      }
      setUser(null);
      setLoading(false);
      return null;
    }
  }

  useEffect(() => {
    const ctrl = new AbortController();
    refreshMe(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  async function login(email, password, { rememberMe = false } = {}) {
    const { data } = await api.post("/auth/login", {
      email, password, remember_me: !!rememberMe,
    });
    setUser(data);
    return data;
  }

  async function register(body) {
    const { data } = await api.post("/auth/register", body);
    if (!data.pending_approval) setUser(data);
    return data;
  }

  async function logout() {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshMe, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
